import {
  Button,
  ButtonGroup,
  ButtonList,
  DropdownButton,
  DropdownButtonContext,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { twilioActions } from 'app/middleware/twilio/actions/twilioActions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import styles from './contactCallAction.module.scss';

interface ContactCallActionProps {
  user: User;
}

export function ContactCallAction({
  user,
}: ContactCallActionProps): JSX.Element {
  const dispatch = useAppDispatch();
  const isInPhoneConversation = !!useAppSelector((state) =>
    state.presence.presence.presence_status.load_descriptions.find(
      (item) => item.media_type === 'speak',
    ),
  );
  const { selectedFromNumber, isMicrophoneBlocked } = useAppSelector(
    (state) => state.telephony,
  );

  const canMakePhoneCall =
    !isInPhoneConversation && !isMicrophoneBlocked && !!selectedFromNumber;

  const disabledButtonTooltipText = useMemo(() => {
    if (!selectedFromNumber) {
      return 'Select a default outbound number in settings to enable phone calls';
    }

    if (isMicrophoneBlocked) {
      return 'Enable the microphone in your browser to enable phone calls';
    }

    if (isInPhoneConversation) {
      return 'End your current call before making new calls';
    }

    return '';
  }, [isInPhoneConversation, selectedFromNumber, isMicrophoneBlocked]);

  const handleClick = (phoneNumber: string | null) => {
    if (!phoneNumber) return;

    const { id } = user;

    if (canMakePhoneCall && selectedFromNumber) {
      dispatch(
        twilioActions.twilioMakeCall({
          userId: id,
          toNumber: phoneNumber,
          fromNumber: selectedFromNumber.e164_format,
        }),
      );

      ampli.contactsCardMakeCallClicked();
    }
  };

  return (
    <ButtonGroup variant="secondary">
      <Tooltip
        content={disabledButtonTooltipText}
        disabled={canMakePhoneCall}
        side="left"
      >
        <Button
          icon="phone"
          disabled={!canMakePhoneCall}
          onClick={() => handleClick(user.phone_number)}
          variant="secondary"
          className={styles.noBorderRadiusRight}
        >
          Call
        </Button>
      </Tooltip>
      {user.additional_phone_numbers && (
        <DropdownButton
          icon="angle-down"
          buttonProps={{
            className: styles.dropdownButton,
          }}
          dropdown={
            <DropdownButtonContext.Consumer>
              {({ toggleDropdown }) => (
                <ButtonList>
                  <ButtonList.Item
                    onClick={() => {
                      toggleDropdown();
                      handleClick(user.phone_number);
                    }}
                  >
                    {user.phone_number}
                  </ButtonList.Item>
                  {user.additional_phone_numbers?.map((option) => (
                    <ButtonList.Item
                      key={option}
                      onClick={() => {
                        toggleDropdown();
                        handleClick(option);
                      }}
                    >
                      {option}
                    </ButtonList.Item>
                  ))}
                </ButtonList>
              )}
            </DropdownButtonContext.Consumer>
          }
        />
      )}
    </ButtonGroup>
  );
}
